// The network parts of an attached deployment's plan review (spec 11 6.15.16, mock n07): the Network chip group, the
// interruption warnings and the network section with the spoke's changes and what the connect run does.
import { Info, Network as NetworkIcon, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import type {
  ChangeAction,
  NetworkStep,
  PlannedNetworkRequest,
  RunChanges,
  RunInterruption,
  SharedChange,
} from '../../api/types';
import { actionPresentation } from '../../components/presentation';
import { StatusBadge } from '../../components/ui';
import { pulumiTypeLabel } from '../../lib/planReview';
import { strings } from '../../strings';
import { DecisionBadge, RequestStateBadge } from '../requests/parts';
import { useManageNetworks } from '../shared';
import { blockedRequests, closingRequests, regionsLine } from './runHelpers';

const r = strings.networks.runs;
const rq = strings.networks.requests;

/**
 * The plan's request warnings at the top (spec 11 6.15.23, mock n07): what the apply closes (warning, with View requests),
 * what stays blocked until a network admin approves it (Approve… for network admins), and the new requests it proposes.
 */
export function RequestCallouts({
  requests,
  networkId,
}: {
  requests: readonly PlannedNetworkRequest[];
  networkId: string | null;
}) {
  const admin = useManageNetworks();
  const closing = closingRequests(requests);
  const blocked = blockedRequests(requests);
  const fresh = requests.filter((x) => x.change === 'new');
  const traffic = networkId ? `/networks/${networkId}?tab=traffic` : null;
  return (
    <>
      {closing.map((text) => (
        <p key={text} className="callout callout-warning" data-testid="plan-request-closes">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">{text}</span>
          {traffic && (
            <Link className="btn btn-sm" to={traffic}>
              {rq.viewRequests}
            </Link>
          )}
        </p>
      ))}
      {blocked.map((x) => (
        <p
          key={`blocked-${x.requestId}-${x.region}`}
          className="callout callout-warning"
          data-testid="plan-request-blocked"
        >
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">
            <code className="font-mono">{x.requestId}</code>: {rq.blockedUntil(x.networkName)}{' '}
            <code className="font-mono text-xs">NETWORK_REQUEST_PENDING</code>
          </span>
          {admin && traffic && (
            <Link className="btn btn-sm" to={`${traffic}&decision=pending`}>
              {rq.approveEllipsis}
            </Link>
          )}
        </p>
      ))}
      {fresh.length > 0 && blocked.length === 0 && (
        <p className="callout callout-info" data-testid="plan-request-new">
          <Info aria-hidden className="size-4 shrink-0" />
          <span>{rq.newRequestsCallout(fresh.length)}</span>
        </p>
      )}
    </>
  );
}

/** The network section's "Network requests" group: new, changed, removed, pending and approved instances. */
function RequestPlanGroup({ requests }: { requests: readonly PlannedNetworkRequest[] }) {
  if (!requests.length) return null;
  return (
    <>
      <h3 className="net-plan-subhead">{rq.planGroup}</h3>
      <ul className="net-plan-rows" data-testid="plan-network-requests">
        {requests.map((x) => {
          const alias = x.requestId.toUpperCase().replace(/-/g, '_');
          const decision =
            x.change === 'removed' ? x.appliedDecision : (x.proposedDecision ?? x.appliedDecision);
          return (
            <li key={`${x.requestId}/${x.provider}/${x.region}`}>
              <span className="badge tone-neutral">{rq.planChange[x.change]}</span>
              <span className="font-mono font-semibold">{x.requestId}</span>
              <span>
                {x.sourceResourceId} → {x.target ? `${x.target.name} / ${x.target.resourceId}` : rq.noTarget}
                {x.port ? ` · ${rq.tcp(x.port)}` : ''}
              </span>
              {x.change !== 'removed' && x.proposedDecision === 'pending' ? (
                <span className={`badge ${x.enforcing ? 'tone-warning' : 'tone-neutral'}`}>
                  {rq.proposedNeedsDecision}
                </span>
              ) : decision ? (
                <DecisionBadge decision={decision} />
              ) : null}
              {x.enforcement && x.appliedDecision && x.change !== 'new' && (
                <RequestStateBadge
                  state={x.enforcing ? x.enforcement : rq.notEnforcedShort}
                  enforcement={x.enforcement}
                  enforcing={x.enforcing}
                  decision={x.appliedDecision}
                />
              )}
              <span className="text-[12px] text-fg-muted">{rq.planSettings(alias)}</span>
            </li>
          );
        })}
      </ul>
    </>
  );
}
const CHANGE_ACTIONS = ['create', 'update', 'replace', 'delete'] as const;
const GLYPH: Record<(typeof CHANGE_ACTIONS)[number], string> = {
  create: '+',
  update: '~',
  replace: '±',
  delete: '−',
};

/** "Network +5 −2": the spoke group's counts as toggles (the same glyphs and tones as the resource chips). */
export function NetworkChips({
  counts,
  selected,
  onToggle,
}: {
  counts: RunChanges;
  selected: ReadonlySet<ChangeAction>;
  onToggle: (action: ChangeAction) => void;
}) {
  const shown = CHANGE_ACTIONS.filter((a) => counts[a] > 0);
  if (shown.length === 0) return null;
  return (
    <div
      role="group"
      aria-label={r.networkChips}
      className="flex flex-wrap items-center gap-2"
      data-testid="network-chips"
    >
      <span aria-hidden className="text-xs text-fg-muted">
        {r.networkChips}
      </span>
      {shown.map((action) => (
        <button
          key={action}
          type="button"
          className={`plan-chip tone-${actionPresentation[action].tone}`}
          aria-pressed={selected.has(action)}
          aria-label={r.networkChangeCount(strings.status.action[action], counts[action])}
          onClick={() => onToggle(action)}
        >
          <span className="glyph" aria-hidden>
            {GLYPH[action]}
          </span>
          {counts[action]}
        </button>
      ))}
    </div>
  );
}

/** Each `interruptions` entry as a warning callout at the top of the review. */
export function InterruptionCallouts({ interruptions }: { interruptions: readonly RunInterruption[] }) {
  return (
    <>
      {interruptions.map((x) => (
        <p
          key={`${x.provider}/${x.region}`}
          className="callout callout-warning"
          data-testid="plan-interruption"
        >
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <span>{x.text}</span>
        </p>
      ))}
    </>
  );
}

/** The network section (`h2` "Network"), after the Changes table. */
export function NetworkPlanSection({
  deployment,
  network,
  where,
  spoke,
  selected,
  steps,
  requests = [],
}: {
  deployment: string;
  network: string;
  /** Feature spec 17, phase 2: the plan's `networkRequests`. */
  requests?: readonly PlannedNetworkRequest[];
  /** The (provider, region) pairs the deployment attaches in. */
  where: readonly { provider: string; region: string }[];
  spoke: readonly SharedChange[];
  /** The Network chips that are on (none: every change but unchanged ones). */
  selected: ReadonlySet<ChangeAction>;
  /** `networkSteps`; null or undefined: they couldn't be computed. */
  steps: readonly NetworkStep[] | null | undefined;
}) {
  const line = regionsLine(where);
  const rows = spoke.filter((c) => (selected.size ? selected.has(c.action) : c.action !== 'no-change'));
  return (
    <section aria-labelledby="plan-network-heading" className="net-plan-section" data-testid="plan-network">
      <div className="flex flex-wrap items-center gap-2">
        <NetworkIcon aria-hidden className="size-4 text-fg-muted" />
        <h2 id="plan-network-heading" className="text-[15px] font-semibold">
          {r.networkSection}
        </h2>
        <span className="text-sm text-fg-muted">{r.attachTo(network, line.provider, line.regions)}</span>
      </div>
      <h3 className="net-plan-subhead">{r.spoke(deployment)}</h3>
      {spoke.length === 0 ? (
        <p className="help">{r.noSpokeChanges}</p>
      ) : rows.length === 0 ? (
        <p className="help">{r.noSpokeRows}</p>
      ) : (
        <ul className="net-plan-rows">
          {rows.map((c) => {
            const type = c.pulumiType.split(':').pop() ?? c.pulumiType;
            return (
              <li key={`${c.provider}:${c.pulumiType}:${c.name}`}>
                <StatusBadge presentation={actionPresentation[c.action]} />
                <span className="font-mono font-semibold">{c.name}</span>
                <span className="text-fg-muted">{r.typeLabels[type] ?? pulumiTypeLabel(c.pulumiType)}</span>
              </li>
            );
          })}
        </ul>
      )}
      <h3 className="net-plan-subhead">{r.connects(deployment, network)}</h3>
      {steps ? (
        steps.length === 0 ? (
          <p className="help">{r.noSteps}</p>
        ) : (
          <ul className="list-disc pl-5 text-sm" data-testid="network-steps">
            {steps.map((step, i) => (
              <li key={`${step.componentKey}-${i}`}>{step.text}</li>
            ))}
          </ul>
        )
      ) : (
        <p className="help" data-testid="network-steps-unavailable">
          {strings.networks.errors.stepsUnavailable}
        </p>
      )}
      <RequestPlanGroup requests={requests} />
    </section>
  );
}
